import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { HighlightOffRounded, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import DocumentTypeTreeView from '../custom-components/DocumentTypeTreeView';
import * as _ from 'lodash';
import { DocumentTemplateType, ICompany } from '../../api-client/autogenerated';
import { GreenCheck } from '../custom-components/CustomCheckboxes';
import { CancelButton } from '../custom-components/CustomButtons';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import { Dayjs } from 'dayjs';
import { useSelector } from 'react-redux';
import { getProjectUsersState } from '../../features/project/selectors';
import CompanyUserTreeView from '../form-pages/user-groups/CompanyUserTreeView';
import { getCompaniesState } from '../../features/companies/selector';
import { ManagePermissionsDialogType } from '../design/ManagePermissionsDialog';
import ProjectAutocomplete from '../custom-components/ProjectAutocomplete';

export interface DocumentSearchFiltersProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    selectedProjectIds: string[],
    documentTemplateNames: (DocumentTemplateType | string)[],
    includeEmails: boolean,
    statuses: DocumentSearchStatusOptionType[],
    createdAfterDate: Dayjs | null,
    createdBeforeDate: Dayjs | null,
    creatorUserIds: string[],
    assignedToUserIds: string[],
  ) => void;
  defaultFilters?: {
    selectedProjectIds: string[];
    documentTypes: (DocumentTemplateType | string)[];
    includeEmails: boolean;
    statuses: DocumentSearchStatusOptionType[];
    createdAfterDate: Dayjs | null;
    createdBeforeDate: Dayjs | null;
    creatorUserIds: string[];
    assignedToUserIds: string[];
  };
}

const useStyles = makeStyles(() =>
  createStyles({
    titleContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '8px 16px 8px 16px',
      borderBottom: '1px solid #EDECEC',
    },
    title: {
      marginTop: '3px',
      marginLeft: '6px',
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '26px',
      lineHeight: '30px',
      textAlign: 'left',
      color: '#0947B9',
      textTransform: 'uppercase',
    },
    rootIconButton: {
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    dialogContent: {
      padding: 16,
    },
    appBar: {
      position: 'relative',
      backgroundColor: '#0947B9',
    },
    mobileTitle: {
      flex: 1,
      fontWeight: 700,
    },
  }),
);

export enum DocumentSearchStatusOptionType {
  AWAITING_SUBMISSION = 'Awaiting Submission',
  SUBMITTED_FOR_REVIEW = 'Submitted for Review',
  UNDER_REVIEW = 'Under Review / In Progress',
  REVIEW_COMPLETE = 'Review Complete / Completed',
  RESUBMITTED = 'Resubmitted / Revise & Resubmit',
  RESOLVED = 'Resolved / Accepted',
}

export default function DocumentSearchFilters(props: DocumentSearchFiltersProps) {
  const { open, onClose, onSubmit, defaultFilters } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const companies = useSelector(getCompaniesState);
  const projectUsers = useSelector(getProjectUsersState);

  const [includeEmails, setIncludeEmails] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<
    (DocumentTemplateType | string)[]
  >([]);
  const [creatorUserIds, setCreatorUserIds] = useState<string[]>([]);
  const [assignedToUserIds, setAssignedToUserIds] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<DocumentSearchStatusOptionType[]>([]);
  const [createdAfterDate, setCreatedAfterDate] = useState<Dayjs | null>(null);
  const [createdBeforeDate, setCreatedBeforeDate] = useState<Dayjs | null>(null);
  const [eligibleCompanies, setEligibleCompanies] = useState<ICompany[]>([]);
  const [createdByOpen, setCreatedByOpen] = useState(false);
  const [assignedToOpen, setAssignedToOpen] = useState(false);

  const isDateRangeInvalid = useMemo(() => {
    if (createdAfterDate && createdBeforeDate) {
      return createdBeforeDate.isBefore(createdAfterDate);
    }
    return false;
  }, [createdAfterDate, createdBeforeDate]);

  useEffect(() => {
    setEligibleCompanies(
      companies
        .map((company) => {
          return {
            ...company,
            users: company.users?.filter(({ id }) =>
              projectUsers.some(({ userId }) => userId === id),
            ),
          };
        })
        .filter(({ users }) => users && users.length > 0),
    );
  }, [companies, projectUsers]);

  useEffect(() => {
    if (!open || !defaultFilters) return;

    setSelectedProjectIds(defaultFilters.selectedProjectIds ?? []);
    setSelectedDocumentTypes(defaultFilters.documentTypes ?? []);
    setIncludeEmails(defaultFilters.includeEmails ?? false);
    setSelectedStatuses(defaultFilters.statuses ?? []);
    setCreatedAfterDate(defaultFilters.createdAfterDate ?? null);
    setCreatedBeforeDate(defaultFilters.createdBeforeDate ?? null);
    setCreatorUserIds(defaultFilters.creatorUserIds ?? []);
    setAssignedToUserIds(defaultFilters.assignedToUserIds ?? []);
  }, [open, defaultFilters]);

  const handleCheckCompany = (
    checked: boolean,
    companyId: string,
    field: 'creator' | 'assigned',
  ) => {
    const setState = field === 'creator' ? setCreatorUserIds : setAssignedToUserIds;
    if (checked)
      setState((prev) =>
        _.uniq([
          ...prev,
          ...(eligibleCompanies.find(({ id }) => id === companyId)?.users?.map(({ id }) => id) ||
            []),
        ]),
      );
    else
      setState((prev) =>
        prev.filter(
          (userId) =>
            !eligibleCompanies
              .find(({ id }) => id === companyId)
              ?.users?.some(({ id }) => id === userId),
        ),
      );
  };

  const handleCheckNestedUser = (
    checked: boolean,
    userId: string,
    field: 'creator' | 'assigned',
  ) => {
    const setState = field === 'creator' ? setCreatorUserIds : setAssignedToUserIds;
    if (checked) setState((prev) => [...prev, userId]);
    else setState((prev) => prev.filter((id) => id !== userId));
  };

  const handleApply = () => {
    if (isDateRangeInvalid) return;
    onSubmit(
      selectedProjectIds,
      selectedDocumentTypes,
      includeEmails,
      selectedStatuses,
      createdAfterDate,
      createdBeforeDate,
      creatorUserIds,
      assignedToUserIds,
    );
    onClose();
  };

  const dialogContent = (
    <MuiPickersUtilsProvider utils={DayjsUtils}>
      <Typography style={{ fontWeight: 600, marginBottom: 16, marginTop: 8 }}>Projects</Typography>
      <ProjectAutocomplete
        selectedProjectIds={selectedProjectIds}
        setSelectedProjectIds={setSelectedProjectIds}
        projectFilter={(p) => p.canSearch}
        style={{ marginBottom: 16 }}
      />
      <Typography style={{ fontWeight: 600, marginBottom: 16, marginTop: 8 }}>
        Document Type
      </Typography>
      <DocumentTypeTreeView
        selectedDocumentTypes={selectedDocumentTypes}
        setSelectedDocumentTypes={setSelectedDocumentTypes}
        includeEmails={includeEmails}
        setIncludeEmails={setIncludeEmails}
      />

      <div
        onClick={() => setCreatedByOpen((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 16,
          cursor: 'pointer',
        }}
      >
        <Typography style={{ fontWeight: 600 }}>Created By</Typography>
        {createdByOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </div>
      <Collapse in={createdByOpen}>
        <CompanyUserTreeView
          type={ManagePermissionsDialogType.None}
          companies={eligibleCompanies}
          handleCheckCompany={(_event, checked, companyId) =>
            handleCheckCompany(checked, companyId, 'creator')
          }
          handleCheckNestedUser={(_event, checked, userId) =>
            handleCheckNestedUser(checked, userId, 'creator')
          }
          selectedUsers={creatorUserIds}
        />
      </Collapse>

      <div
        onClick={() => setAssignedToOpen((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 16,
          cursor: 'pointer',
        }}
      >
        <Typography style={{ fontWeight: 600 }}>Assigned To</Typography>
        {assignedToOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </div>
      <Collapse in={assignedToOpen}>
        <CompanyUserTreeView
          type={ManagePermissionsDialogType.None}
          companies={eligibleCompanies}
          handleCheckCompany={(_event, checked, companyId) =>
            handleCheckCompany(checked, companyId, 'assigned')
          }
          handleCheckNestedUser={(_event, checked, userId) =>
            handleCheckNestedUser(checked, userId, 'assigned')
          }
          selectedUsers={assignedToUserIds}
        />
      </Collapse>

      <Typography style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}>Status</Typography>
      {Object.values(DocumentSearchStatusOptionType).map((status) => (
        <div key={status} style={{ display: 'flex', marginLeft: 2 }}>
          <GreenCheck
            style={{ padding: 0 }}
            checked={selectedStatuses.includes(status)}
            onChange={(_event, checked) =>
              checked
                ? setSelectedStatuses((prev) => [...prev, status])
                : setSelectedStatuses((prev) => prev.filter((s) => s !== status))
            }
          />
          <Typography style={{ height: 30, display: 'flex', alignItems: 'center', paddingLeft: 9 }}>
            {status}
          </Typography>
        </div>
      ))}

      <Typography style={{ fontWeight: 600, marginTop: 16, marginBottom: 16 }}>
        Created Date
      </Typography>
      <div
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <KeyboardDatePicker
          inputVariant="outlined"
          label="From"
          format="MMMM D, YYYY"
          value={createdAfterDate}
          onChange={(date) => setCreatedAfterDate(date)}
        />
        <KeyboardDatePicker
          inputVariant="outlined"
          label="To"
          format="MMMM D, YYYY"
          value={createdBeforeDate}
          onChange={(date) => setCreatedBeforeDate(date)}
          error={isDateRangeInvalid}
          helperText={isDateRangeInvalid ? 'End must be after start' : undefined}
        />
      </div>
    </MuiPickersUtilsProvider>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth={!isMobile}
      maxWidth={isMobile ? undefined : 'sm'}
    >
      {isMobile ? (
        <>
          <AppBar className={classes.appBar} elevation={1}>
            <Toolbar>
              <Typography variant="h6" className={classes.mobileTitle}>
                Filters
              </Typography>
              <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <div className={classes.dialogContent}>{dialogContent}</div>
          <div style={{ display: 'inline-flex', flexGrow: 100 }} />
          <DialogActions
            style={{
              justifyContent: 'space-between',
              marginBottom: 16,
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            <CancelButton onClick={onClose}>Cancel</CancelButton>
            <Button
              color="primary"
              variant="contained"
              onClick={handleApply}
              disabled={isDateRangeInvalid}
            >
              Apply
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle className={classes.titleContainer}>
            <Typography className={classes.title} align="left">
              Filters
            </Typography>
            <IconButton
              style={{ right: '20px', top: '12px', position: 'absolute' }}
              onClick={onClose}
              classes={{
                root: classes.rootIconButton,
              }}
            >
              <HighlightOffRounded />
            </IconButton>
          </DialogTitle>
          <DialogContent>{dialogContent}</DialogContent>
          <DialogActions style={{ justifyContent: 'space-between', padding: 16 }}>
            <CancelButton onClick={onClose}>Cancel</CancelButton>
            <Button
              color="primary"
              variant="contained"
              onClick={handleApply}
              disabled={isDateRangeInvalid}
            >
              Apply
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
